import apiClient from './api';

export type DomainStatus = 'PENDING_DNS' | 'PENDING_PAYMENT' | 'REGISTERING' | 'ACTIVE' | 'FAILED' | 'EXPIRED' | 'DISCONNECTED';

export interface DomainRow {
    id: string;
    name: string;
    tld: string;
    source: 'EXTERNAL' | 'NAMESILO';
    status: DomainStatus;
    applicationId: string | null;
    applicationName: string | null;
    /** True when the edge serves the app at this domain right now. */
    live: boolean;
    expiresAt: string | null;
    lastError: string | null;
    createdAt: string;
}

export interface DnsInstruction {
    type: 'TXT' | 'A' | 'CNAME';
    host: string;
    value: string;
    purpose: string;
    /** Not required for the site to go live (apex record — www alone suffices). */
    optional?: boolean;
}

export interface SearchResult {
    domain: string;
    tld: string;
    available: boolean;
    premium: boolean;
    priceUsd: number | null;
    priceIdr: number | null;
    renewIdr: number | null;
}

export interface RegistrantContact {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    company?: string;
}

export interface DomainOrderStart {
    token: string;
    redirect_url: string;
    order_id: string;
    amount: number;
    domain: string;
    years: number;
}

export interface DomainOrderView {
    orderId: string;
    kind: 'REGISTER' | 'RENEW';
    status: 'PENDING' | 'PAID' | 'FULFILLED' | 'FAILED' | 'NEEDS_ATTENTION';
    amount: number;
    years: number;
    error: string | null;
    domain: DomainRow;
}

const msg = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

/** Custom domains: connect one you own, or buy .com / .id through HyVay. */
export const domainsService = {
    async list(applicationId?: string): Promise<{ domains: DomainRow[]; buyEnabled: boolean }> {
        const res = await apiClient.get<{ data: DomainRow[]; buyEnabled: boolean }>('/domains', { params: applicationId ? { applicationId } : {} });
        return { domains: res.data.data, buyEnabled: !!res.data.buyEnabled };
    },

    async connect(applicationId: string, domain: string): Promise<{ domain: DomainRow; records: DnsInstruction[] }> {
        try {
            const res = await apiClient.post<{ data: { domain: DomainRow; records: DnsInstruction[] } }>('/domains/connect', { applicationId, domain });
            return res.data.data;
        } catch (e: any) {
            throw new Error(msg(e, 'Could not connect the domain.'));
        }
    },

    async instructions(id: string): Promise<{ domain: DomainRow; records: DnsInstruction[] }> {
        const res = await apiClient.get<{ data: { domain: DomainRow; records: DnsInstruction[] } }>(`/domains/${id}/instructions`);
        return res.data.data;
    },

    async verify(id: string): Promise<{ ok: boolean; checks: { txt: boolean; apex: boolean; www: boolean }; domain: DomainRow; records: DnsInstruction[] }> {
        try {
            const res = await apiClient.post<{ data: any }>(`/domains/${id}/verify`);
            return res.data.data;
        } catch (e: any) {
            throw new Error(msg(e, 'Could not check the domain.'));
        }
    },

    async disconnect(id: string): Promise<void> {
        try {
            await apiClient.delete(`/domains/${id}`);
        } catch (e: any) {
            throw new Error(msg(e, 'Could not detach the domain.'));
        }
    },

    async attach(id: string, applicationId: string): Promise<DomainRow> {
        try {
            const res = await apiClient.post<{ data: DomainRow }>(`/domains/${id}/attach`, { applicationId });
            return res.data.data;
        } catch (e: any) {
            throw new Error(msg(e, 'Could not attach the domain.'));
        }
    },

    async search(q: string): Promise<{ enabled: boolean; results: SearchResult[] }> {
        try {
            const res = await apiClient.get<{ data: { enabled: boolean; results: SearchResult[] } }>('/domains/search', { params: { q } });
            return res.data.data;
        } catch (e: any) {
            throw new Error(msg(e, 'Search failed.'));
        }
    },

    async createOrder(input: { domain: string; applicationId?: string; years?: number; contact: RegistrantContact }): Promise<DomainOrderStart> {
        try {
            const res = await apiClient.post<{ data: DomainOrderStart }>('/domains/orders', input);
            return res.data.data;
        } catch (e: any) {
            throw new Error(msg(e, 'Could not start the order.'));
        }
    },

    async renew(id: string, years = 1): Promise<DomainOrderStart> {
        try {
            const res = await apiClient.post<{ data: DomainOrderStart }>(`/domains/${id}/renew`, { years });
            return res.data.data;
        } catch (e: any) {
            throw new Error(msg(e, 'Could not start the renewal.'));
        }
    },

    /** Reconcile with Midtrans and return the order (safe to poll). */
    async order(orderId: string): Promise<DomainOrderView | null> {
        try {
            const res = await apiClient.get<{ data: DomainOrderView }>(`/domains/orders/${encodeURIComponent(orderId)}`);
            return res.data.data;
        } catch {
            return null;
        }
    },
};
